import React, { useEffect, useMemo, useState } from 'react';
import {
	useWindowDimensions,
	View,
	Dimensions,
	StyleSheet,
} from 'react-native';
import FiltersContainer from '../../../components/web/Filters/Filters.container';
import TableContainer from '../../../components/web/ReactTable/Table.container';
import ReportsPageWrapper from '../../../components/web/ReportsPageWrapper/ReportsPageWrapper.component';
import ModalContainer from '../../../components/shared/Modal/Modal.container';
import ImageIcon from '../../../components/shared/Icon/ImageIcon.component';
import { makeStyles } from '@material-ui/core';
import CreateUserModal from '../CreateUpdateUser/CreateAndUpdateUserWeb/CreateUserModal/CreateUserModal.container';
import UsersTablePageNative from './UsersTablePage.component.native';
import { i18nService } from '../../../services/i18n.service';
import I18n from '../../../components/shared/i18n/I18n.component';
import colors from '../../../config/colors';
import useDevicePlatform, {
	DeviceType,
} from '../../../hooks/useDevicePlatform.hook';
import { modalService } from '../../../services/modal.service';
import UpdateMultiplePermissionsDrawer from './UpdateMultiplePermissionsDrawer/UpdateMultiplePermissionsDrawer.component';
import RolesAndPermissionsModal from './RolesAndPermissionsModal/RolesAndPermissionsModal';
import { isRowSelectable, initialHeaders, SelectionMode } from './UsersTablePage.constants';
import SelectionFooter, {
	SELECTION_FOOTER_SPACE,
} from './SelectionFooter.component';
import useBusinessUnitsState from '../../../hooks/useBusinessUnitsState';
import { Privileges } from '../../../constants/roleTypes.constant';
import {
	EventName,
	logEvent,
} from '../../../services/analyticsAndLogs.service';
import ButtonV2 from '../../../components/shared/Buttons/ButtonV2.container';
import InviteUsersDrawer from './InviteUsersDrawer/InviteUsersDrawer.component';
import InviteHistoryDrawer from './InviteHistoryDrawer/InviteHistoryDrawer.component';

type UsersTableProps = {
	navigation: any;
	onFilter: (data: any) => void;
	filtersData: any;
	data: any[];
	totalReports?: number;
	onSort: (field: string, direction: string) => void;
	getExcel: () => void;
	getNextData: () => void;
	errorMessage?: string;
	goBack: () => void;
	modalOpened: boolean;
	modalData: any;
	route: any;
	onModalBtnPressedHandler: () => void;
	userData: any;
	updateUsers: () => void;
	updateRow: (data: any) => void;
	selectedFilters: any;
	refreshPage: () => void;
	businessUnitsList: any[];
	selectedFiltersForMobile: any;
	getSelectedUnits: any;
	docPermission: boolean;
	showCreateUserBtn: boolean;
	modules: any;
	employerRolePrivileges: any;
};

/** breathing room between the bottom of the table and the bottom of the screen */
const TABLE_BOTTOM_GAP = 24;

const useStyles = makeStyles(theme => {
	return {
		root: {
			padding: '0!important',
			minWidth: '20px',

			'&:hover': {
				backgroundColor: 'transparent',
			},
		},
		mainHeader: {
			fontFamily: 'RubikMedium',
			fontSize: 20,
			color: colors.primaryDarkBlue,
		},
		mainHeaderWrapper: {
			display: 'flex',
			flexDirection: 'row-reverse',
			justifyContent: 'space-between',
			width: '100%',
		},
		questionButton: {
			color: colors.primary,
			padding: '0!important',
			position: 'relative',
			left: '-27px',

			'&:hover': {
				backgroundColor: 'transparent',
			},
		},
		questionButtonText: {
			marginRight: '7px',
		},
		buttonsContainer: {
			display: 'flex',
			flexDirection: 'row-reverse',
		},
		addIcon: {
			marginLeft: 2,
			marginTop: 2,
		},
	};
});

const styles = StyleSheet.create({
	actionButton: {
		width: 160,
		height: 40,
		padding: 0,
		marginLeft: 15,
	},
	smallActionButton: {
		width: 160,
		height: 40,
		padding: 0,
		marginLeft: 8,
	},
});

const UsersTablePage = ({
	navigation,
	userData,
	data,
	totalReports,
	filtersData,
	onSort,
	onFilter,
	getNextData,
	getExcel,
	errorMessage,
	modalOpened,
	modalData,
	route,
	onModalBtnPressedHandler,
	updateUsers,
	updateRow,
	selectedFilters,
	refreshPage,
	selectedFiltersForMobile,
	businessUnitsList,
	getSelectedUnits,
	docPermission,
	showCreateUserBtn,
	modules,
	employerRolePrivileges,
}: UsersTableProps) => {
	const { height } = useWindowDimensions();
	const classes = useStyles();

	const [selectedItems, setSelectedItems] = useState<number[]>([]);
	const [selectAll, setSelectAll] = useState(false);
	const [selectionMode, setSelectionMode] = useState<SelectionMode>(null);
	const [inviteDrawerOpen, setInviteDrawerOpen] = useState(false);
	/** the bulk permission-update drawer - open once users are selected */
	const [permissionsDrawerOpen, setPermissionsDrawerOpen] = useState(false);
	/** the row whose invitation history is open - null when the drawer is closed */
	const [historyUser, setHistoryUser] = useState<any>(null);

	const hasActiveUsers = useMemo(
		() => (data || []).some((user: any) => user.status),
		[data],
	);
	const hasInactiveUsers = useMemo(
		() => (data || []).some((user: any) => isRowSelectable(user, 'invite')),
		[data],
	);

	const selectedCount = useMemo(() => {
		if (!selectAll) {
			return selectedItems.length;
		}
		return (data || []).filter((user: any) =>
			isRowSelectable(user, selectionMode),
		).length;
	}, [selectAll, selectedItems, selectionMode, data]);

	/** Drives the header checkbox. Once a row is unchecked selectAll drops to
	 * false and the selection becomes explicit ids, so re-checking that row
	 * would otherwise leave the header unchecked while every visible row is
	 * checked. Display only - the request payload still keys off selectAll. */
	const allLoadedSelected = useMemo(() => {
		if (selectAll) {
			return true;
		}
		const selectable = (data || []).filter((user: any) =>
			isRowSelectable(user, selectionMode),
		);
		return (
			selectable.length > 0 &&
			selectable.every((user: any) =>
				selectedItems.includes(user.profileId),
			)
		);
	}, [selectAll, selectedItems, selectionMode, data]);

	const platform = useDevicePlatform();
	const isMobileWeb = platform === DeviceType.MOBILE_WEB;

	const showComprehensivePermissionUpdateBtn = useMemo(() => {
		if (modules) {
			const modulesForUpdate = Object.keys(modules).find(key =>
				modules[key]?.find(
					(module: any) =>
						module.id === 113 ||
						module.id === 101 ||
						module.id === 102,
				),
			);

			if (modulesForUpdate) {
				return true;
			}
			return false;
		}
		return false;
	}, [modules]);

	const { businessUnitsList: businessUnitsListWrite } = useBusinessUnitsState(
		{
			privileges: [
				Privileges.ROLE_MGM_USER_WRITE,
				Privileges.ROLE_MGM_USER_READ,
				Privileges.ROLE_CREATE_USER_WRITE,
				Privileges.ROLE_CREATE_USER_READ,
			],
		},
	);

	const onRowClick = (rowData: any) => {
		modalService.openModal(
			null,
			{
				...rowData,
				callback: updateRow,
				businessUnitsList: businessUnitsListWrite,
				showLoader: true,
			},
			CreateUserModal,
		);
	};

	const onCreateUserButtonClick = async () => {
		modalService.openModal(
			null,
			{
				callback: updateUsers,
				businessUnitsList: businessUnitsListWrite,
				showLoader: true,
			},
			CreateUserModal,
		);
		logEvent({
			eventName: EventName.addNewUserFromUsersTable,
			data: {},
		});
	};
	const openRolesAndPermissionsModal = () => {
		modalService.openModal(
			null,
			{ hideCloseBtn: true, showLoader: true },
			(props: any) => (
				<RolesAndPermissionsModal
					{...props}
					onClose={props.onNext}
					businessUnitsList={businessUnitsList}
				/>
			),
		);
	};

	const startRefresh = () => {
		setSelectedItems([]);
		setSelectAll(false);
		setSelectionMode(null);
		refreshPage();
	};

	const onUpdateMultiple = async () => {
		setPermissionsDrawerOpen(true);
	};

	const enterSelectionMode = (mode: SelectionMode) => {
		setSelectionMode(mode);
		setSelectedItems([]);
		setSelectAll(false);
	};

	const exitSelectionMode = () => {
		setSelectionMode(null);
		setSelectedItems([]);
		setSelectAll(false);
	};

	const onContinue = () => {
		if (selectionMode === 'permissions') {
			onUpdateMultiple();
		} else if (selectionMode === 'invite') {
			setInviteDrawerOpen(true);
		}
	};

	const onSelectAll = val => {
		setSelectAll(val);
		setSelectedItems([]);
	};

	const onRowsChecked = (v, id, row) => {
		const { profileId } = row.original;
		//handeling unchecking a row when select all is checked.
		if (selectAll) {
			const allSelectable = (data || [])
				.filter((user: any) => isRowSelectable(user, selectionMode))
				.map((user: any) => user.profileId);
			setSelectAll(false);
			setSelectedItems(
				v ? allSelectable : allSelectable.filter(el => el !== profileId),
			);
			return;
		}
		setSelectedItems(
			v
				? [...selectedItems, profileId]
				: selectedItems.filter(el => el !== profileId),
		);
	};

	useEffect(() => {
		if (route?.params?.isFromDashboard) {
			onCreateUserButtonClick();
			navigation.setParams({ isFromDashboard: undefined });
		}
	}, [route.params]);

	return isMobileWeb ? (
		<UsersTablePageNative
			navigation={navigation}
			filtersData={filtersData}
			onFilter={onFilter}
			data={data}
			getNextData={getNextData}
			userData={userData}
			selectedFilters={selectedFilters}
			selectedFiltersForMobile={selectedFiltersForMobile}
			businessUnitsList={businessUnitsList}
			docPermission={docPermission}
			showCreateUserBtn={showCreateUserBtn}
			modules={modules}
		/>
	) : (
		<>
			<ReportsPageWrapper
				navigation={navigation}
				title={
					<div className={classes.mainHeaderWrapper}>
						<div className={classes.mainHeader}>
							{i18nService.translate(
								'usersManagement.table.title',
							)}
						</div>
						<View style={{flexDirection: 'row-reverse', alignItems: 'center', gap: 16 }}>
							<ButtonV2
								onPress={getExcel}

							>
								<I18n color='titlePrimary' size={15} numberOfLines={1}>
									export
								</I18n>
								<ImageIcon 
									name='tableImport'
									style={{
											height: 18,
											width: 18,
											marginLeft: '8px',
										}}
								 />
							</ButtonV2>
							{(userData?.employerRolePrivileges?.includes(
								'ROLE_MGM_USER_READ',
							) ||
								userData?.employerRolePrivileges?.includes(
									'ROLE_MGM_USER_WRITE',
								)) && (
								<ButtonV2
									onPress={openRolesAndPermissionsModal}
								>
									<I18n color='titlePrimary' size={15} numberOfLines={1}>
										usersManagement.table.permissionsButton
									</I18n>
									<ImageIcon
										name='infoCircle'
										style={{
											height: 18,
											width: 18,
											marginLeft: '8px',
										}}
									/>
								</ButtonV2>
							)}
							<ButtonV2
								preset='outline'
								onPress={() => enterSelectionMode('invite')}
								disabled={!hasInactiveUsers}
							>
								<I18n color='titlePrimary' size={15} numberOfLines={1}>
									usersManagement.table.inviteUser
								</I18n>
								<ImageIcon 
									name='send'
									style={{
											height: 16,
											width: 16,
											marginLeft: '8px',
										}}
								 />
							</ButtonV2>
							{showComprehensivePermissionUpdateBtn && (
								<ButtonV2
									preset='outline'
									onPress={() => enterSelectionMode('permissions')}
									disabled={!hasActiveUsers}
								>
									<I18n color='titlePrimary' size={15} numberOfLines={1}>
										usersManagement.updateMultiple
									</I18n>
									<ImageIcon 
										name='shieldLock'
										style={{
												height: 18,
												width: 18,
												marginLeft: '8px',
											}}
									/>
								</ButtonV2>
							)}
							{showCreateUserBtn && (
								<ButtonV2
									preset='primary'
									onPress={onCreateUserButtonClick}
								>
									<I18n color='white' size={15} numberOfLines={1}>
										createUserButton.title
									</I18n>
									<ImageIcon 
										name='plus'
										color='white'
										style={{
												height: 14,
												width: 14,
												marginLeft: '8px',
											}}
									/>
								</ButtonV2>
							)}
						</View>
					</div>
				}
			>
				<View
					style={{
						maxHeight: height - 241 - TABLE_BOTTOM_GAP - (selectionMode ? SELECTION_FOOTER_SPACE : 0),
						minHeight: height - 241 - TABLE_BOTTOM_GAP - (selectionMode ? SELECTION_FOOTER_SPACE : 0),
					}}
				>
					<View
						style={{
							marginBottom: 31,
							zIndex: 3,
							flexDirection: 'row-reverse',
							justifyContent: 'space-between',
						}}
					>
						<FiltersContainer
							hideSearchInput
							config={filtersData}
							onChange={onFilter}
							customStyles={{
								container: {
									flexDirection: 'row-reverse',
								},
								filterContainer: {
									marginRight: 0,
									marginLeft: 15,
									zIndex: 3,
								},
							}}
						/>
						<div className={classes.buttonsContainer}>
							
						</div>
					</View>

					<TableContainer
						getNextData={getNextData}
						// onRowClick={onRowClick}
						headers={initialHeaders(
							selectAll,
							allLoadedSelected,
							onSelectAll,
							onRowsChecked,
							selectedItems,
							onRowClick,
							businessUnitsList,
							employerRolePrivileges,
							docPermission,
							modules,
							selectionMode,
							setHistoryUser,
						)}
						data={data || []}
						outerClasses={{
							tableRoot: classes.tableRoot,
							cellRoot: classes.tableCell,
							tableWrapper: classes.thinScrollbar,
						}}
						count={15}
						keyName={''}
						onSort={onSort}
						hasFooter={false}
						errorMessage={errorMessage}
						resetActiveSortByProps={[userData.id]}
					/>
				</View>
			</ReportsPageWrapper>
			{selectionMode && (
				<SelectionFooter
					count={selectedCount}
					continueDisabled={selectedItems.length === 0 && !selectAll}
					continueLabel={
						selectionMode === 'invite'
							? 'usersManagement.selectionFooter.sendInvite'
							: 'usersManagement.selectionFooter.continueToUpdatePermissions'
					}
					onContinue={onContinue}
					onCancel={exitSelectionMode}
				/>
			)}
			<InviteUsersDrawer
				open={inviteDrawerOpen}
				onClose={() => setInviteDrawerOpen(false)}
				onSent={() => {
					setInviteDrawerOpen(false);
					startRefresh();
				}}
				selectAll={selectAll}
				selectedItems={selectedItems}
				initialCount={selectedCount}
				localUsers={data || []}
				unitId={getSelectedUnits(selectedFilters?.unitIds)?.[0]}
			/>
			<UpdateMultiplePermissionsDrawer
				open={permissionsDrawerOpen}
				onClose={() => setPermissionsDrawerOpen(false)}
				selectAll={selectAll}
				selectedItems={selectedItems}
				selectedFilters={{
					...selectedFilters,
					unitIds: getSelectedUnits(selectedFilters?.unitIds),
				}}
				refreshPage={startRefresh}
			/>
			<InviteHistoryDrawer
				open={!!historyUser}
				user={historyUser}
				unitId={getSelectedUnits(selectedFilters?.unitIds)?.[0]}
				onClose={() => setHistoryUser(null)}
			/>
			<ModalContainer
				visible={modalOpened}
				component={CreateUserModal}
				data={modalData}
				onNext={() => onModalBtnPressedHandler()}
				route={route}
			/>
		</>
	);
};

export default UsersTablePage;
